<script setup lang="ts">
import type { PartnerHistoryItem } from '../types';

defineProps<{
  history: PartnerHistoryItem[];
  isLoading: boolean;
}>();

function iconClass(type: PartnerHistoryItem['type']): string {
  if (type === 'removed') return 'warn';
  return '';
}

function meta(item: PartnerHistoryItem): string {
  if (item.type === 'active') {
    return `${item.startDate} · permanece ativo`;
  }

  if (item.type === 'entered') {
    return item.startDate;
  }

  if (item.type === 'removed') {
    const date = item.endDate ?? item.startDate;
    return item.detail ? `${date} · ${item.detail}` : date;
  }

  const range = item.endDate ? `${item.startDate} → ${item.endDate}` : item.startDate;
  return item.detail ? `${range} · ${item.detail}` : range;
}

function title(item: PartnerHistoryItem): string {
  if (item.type === 'active' || item.type === 'entered') {
    return `Entrou em "${item.campaignName}"`;
  }

  if (item.type === 'removed') return `Removido de "${item.campaignName}"`;

  return `Concluiu "${item.campaignName}"`;
}
</script>

<template>
  <div class="proto-card">
    <div class="proto-card-header">
      <div>
        <div class="proto-card-title">Histórico de jornadas</div>
        <div class="proto-card-sub">Cronologia completa de exposição</div>
      </div>
    </div>

    <div class="proto-card-body">
      <template v-if="isLoading">
        <div v-for="i in 3" :key="i" class="placeholder-glow mb-3">
          <span class="placeholder col-8" style="height: 14px; border-radius: 3px;" />
        </div>
      </template>

      <div v-else-if="history.length === 0" class="empty-state">
        Nenhuma jornada registrada.
      </div>

      <div v-else class="timeline">
        <div v-for="item in history" :key="item.id" class="timeline-item">
          <div class="timeline-icon" :class="iconClass(item.type)">
            <!-- current: clock -->
            <svg
              v-if="item.type === 'active' || item.type === 'entered'"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            <!-- completed: check -->
            <svg
              v-else-if="item.type === 'completed'"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <!-- removed: warning -->
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 9v4" /><path d="M12 17h.01" />
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>

          <div class="timeline-content">
            <div class="timeline-title">{{ title(item) }}</div>
            <div class="timeline-meta">{{ meta(item) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$verde: #00b569;
$verde-bg: #e6f8f0;
$laranja-bg: #fef3c7;
$cinza-borda: #e4e6eb;
$cinza-texto: #6b7280;
$preto: #222222;

.proto-card {
  background: #fff;
  border: 1px solid $cinza-borda;
  border-radius: 12px;
  margin-bottom: 20px;
  overflow: hidden;
}

.proto-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid $cinza-borda;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.proto-card-title {
  font-family: 'Work Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: $preto;
}

.proto-card-sub {
  font-size: 12px;
  color: $cinza-texto;
  margin-top: 2px;
}

.proto-card-body {
  padding: 20px;
}

.empty-state {
  color: $cinza-texto;
  font-size: 13px;
}

.timeline {
  padding: 8px 0;
}

.timeline-item {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  padding: 10px 0;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 17px;
    top: 36px;
    bottom: -10px;
    width: 2px;
    background: $cinza-borda;
  }
}

.timeline-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: $verde-bg;
  color: $verde;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  flex-shrink: 0;

  &.warn {
    background: $laranja-bg;
    color: #b45309;
  }
}

.timeline-content {
  padding-top: 6px;
}

.timeline-title {
  font-size: 13.5px;
  font-weight: 600;
  color: $preto;
}

.timeline-meta {
  font-size: 12px;
  color: $cinza-texto;
  margin-top: 2px;
}
</style>
